import { useMemo, useState } from "react";

import { useOnlyOnce } from "@nens/react/hooks";

import { useDatasetByLizardIdQueryDirectly } from "@/queries/query-dataset";

import { ObjectSchema } from "../types/object-schema";
import { ProcessRunChildProps } from "../types/process-props";
import { convertSchemaToRSJF } from "../utils/convert-schema-to-rsjf";
import { filterOutputSchemaForForm } from "../utils/filter-output-schema";

export function useProcessRunSchemas({ process }: ProcessRunChildProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [inputSchema, setInputSchema] = useState<ObjectSchema | undefined>(undefined);
  const [resultOutputSchema, setResultOutputSchema] = useState<ObjectSchema | undefined>(undefined);
  const datasetByLizardIdQueryDirectly = useDatasetByLizardIdQueryDirectly();

  useOnlyOnce(async () => {
    const processInputSchema = { ...process.inputSchema, "ui:order": process.inputOrder };
    const processOutputSchema = { ...process.outputSchema, "ui:order": process.outputOrder };

    const [inputSchema, outputSchema] = await Promise.all([
      convertSchemaToRSJF(processInputSchema, datasetByLizardIdQueryDirectly),
      convertSchemaToRSJF(processOutputSchema, datasetByLizardIdQueryDirectly),
    ]);

    setIsLoading(false);

    // Inputs can just be converted and added to the state.
    if (inputSchema) setInputSchema(inputSchema);

    if (!outputSchema) return;

    setResultOutputSchema(outputSchema);
  });

  // Not all outputs need to be configured by the user, so filter them.
  const outputSchema = useMemo(
    () => (resultOutputSchema ? filterOutputSchemaForForm(resultOutputSchema) : undefined),
    [resultOutputSchema]
  );

  return {
    isLoading,
    inputSchema,
    outputSchema,
    resultOutputSchema,
  };
}
